import { toJS } from 'mobx';
import Logger from '../../../services/Logger';
import {
  ARTICLE_FIRE,
  CHANGE_SITE_COLOR,
  CHANGE_SITE_COLORS,
  CHANGE_SITE_FONTS,
  CHANGE_SITE_LOGO,
  COLLECT_ALL_AREAS,
  COLLECT_ALL_AREAS_SUCCESS,
  COLLECT_CHANGED_BY_USER_SITE_DATA,
  COLLECT_CHANGED_BY_USER_SITE_DATA_SUCCESS,
  ELEMENT_CHANGED,
  ELEMENT_CLICKED,
  ELEMENT_MOUSEOVER,
  LOCKED_ELEMENT_MOUSEOVER,
  ELEMENTS_CHANGE_POSITIONS,
  ELEMENTS_INITIALIZED,
  GET_INITIAL_ELEMENTS,
  GET_SECTION_EDITABLE_ELEMENTS,
  INITIALIZE_WIZARD_SETTINGS,
  INITIALIZE_WIZARD_SETTINGS_SUCCESS,
  SECTION_CLICKED,
  SECTION_HOVERED,
  SECTION_EDITABLE_ELEMENTS,
  SLIDE_ANIMATION_END,
  SLIDE_ANIMATION_START,
  SLIDE_ANIMATION_START_AFTER_DELAY,
  TEXT_EDITOR_BLUR,
  UPDATE_AREA_TEXT,
  WIZARD_BRIDGE_INITIALIZED,
  WIZARD_INIT,
  APPLY_NAVIGATION_EDIT_STYLE
} from '../consts/events';
import SiteBuilderModel from '../model/SiteBuilderModel';
import IframeManager from '../services/frames/IframeManager';
import PageBuilderDataCollector from '../services/PageBuilderDataCollector';
import WrapperSynchronizer from '../services/WrapperSynchronizer';
import IframeEventsManager from '../services/frames/IframeEventsManager';
import IframeServiceInjector from '../services/injectors/IframeServiceInjector';
import FrameEventMessanger from '../services/frames/FrameEventMessanger';
import SiteMapModel from '../../Sitemap/models/SiteMapModel';
import DesignModel from '../../Design/models/DesignModel';
import FinalizeSitemapModel from '../../FinalizeSitemap/models/FinalizeSitemapModel';
import BusinessDescriptionModel from '../../BusinessDescription/models/BusinessDescriptionModel';
import EditorStateMachine from '../model/EditorStateMachine';
import UserPreviewDataModel from '../model/user-preview-data/UserPreviewDataModel';
import SocialMediaModel from '../../SocialMedia/models/SocialMediaModel';
import PictureModel from '../model/picture/PictureModel';

export default function configEvents() {

  IframeEventsManager.addEvent(ELEMENTS_INITIALIZED, (event) => {
    const { elPositions } = event.data.payload;
    Logger.log('IframeEventsManager', 'ELEMENTS_INITIALIZED', elPositions);
    // store elements
    EditorStateMachine.initializeElements(elPositions);
  });

  IframeEventsManager.addEvent(ELEMENTS_CHANGE_POSITIONS, (event) => {
    const { newPositions } = event.data.payload;

    Logger.log(
      'IframeEventsManager',
      'ELEMENTS_CHANGE_POSITIONS',
      newPositions
    );

    const WrapperSynchronizer =
      IframeServiceInjector.getService<WrapperSynchronizer>(
        'WrapperSynchronizer'
      );

    if (EditorStateMachine.hoveredElementId) {
      WrapperSynchronizer.updateElementsPosition(
        EditorStateMachine.hoveredElementId,
        newPositions.get(EditorStateMachine.hoveredElementId).elementPosition
      );
    }

    newPositions.forEach((element, elementId) => {
      WrapperSynchronizer.updateElementsPosition(
        elementId,
        element.elementPosition
      );
      
      const isSelectedSection = elementId === EditorStateMachine.selectedSectionId;

      if (isSelectedSection) {
        // Update the section overlay position
        WrapperSynchronizer.updateElementsPosition(
          `section-${elementId}`,
          element.elementPosition
        );
        EditorStateMachine.updateSectionPosition(element.elementPosition);
        
        if (EditorStateMachine.isSectionInEditMode && 
            elementId === EditorStateMachine.editModeSectionId && 
            EditorStateMachine.lockedSectionElements.length > 0) {
          // Request updated positions for locked elements from the renderer
          IframeServiceInjector.getService<FrameEventMessanger>(
            'FrameEventMessanger'
          ).sendMessage(GET_SECTION_EDITABLE_ELEMENTS, {
            sectionId: EditorStateMachine.editModeSectionId,
            updatePositionsOnly: true  // Flag to only update positions, not full element list
          });
        }
      }
    });

    if (EditorStateMachine.selectedSectionId === -1) {
      if (EditorStateMachine.isSectionInEditMode && 
          EditorStateMachine.editModeSectionId === -1 && 
          EditorStateMachine.lockedSectionElements.length > 0) {
        IframeServiceInjector.getService<FrameEventMessanger>(
          'FrameEventMessanger'
        ).sendMessage(GET_SECTION_EDITABLE_ELEMENTS, {
          sectionId: -1,
          updatePositionsOnly: true
        });
      }
    }
  });

  IframeEventsManager.addEvent(ELEMENT_MOUSEOVER, (event) => {
    const { elementId, elementPosition } = event.data.payload;

    if (EditorStateMachine.isSectionInEditMode) {
      const isEditableElement = EditorStateMachine.editableSectionElements.some(
        el => el.elementId === elementId
      );
      
      if (isEditableElement) {
        EditorStateMachine.transition('onElementHover', {
          elementId,
          elementPosition
        });
      }
    }
  });

  IframeEventsManager.addEvent(LOCKED_ELEMENT_MOUSEOVER, (event) => {
    const { elementType } = event.data.payload;
    
    EditorStateMachine.setHoveredLockedElement(elementType);
  });

  IframeEventsManager.addEvent(SECTION_CLICKED, (event) => {
    const { sectionId, sectionPosition } = event.data.payload;
    
    // Only use click for touch devices as fallback
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    if (isTouchDevice) {
      if (sectionId !== null && sectionPosition) {
        EditorStateMachine.selectSection(sectionId, sectionPosition);
      } else {
        EditorStateMachine.deselectSection();
      }
    }
  });

  IframeEventsManager.addEvent(SECTION_HOVERED, (event) => {
    const { sectionId, sectionPosition } = event.data.payload;
    
    // Only use hover for non-touch devices
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    if (!isTouchDevice) {
      if (sectionId !== null && sectionPosition) {
        if (EditorStateMachine.dismissedSectionId !== null) {
          EditorStateMachine.clearDismissedSection();
        }
        
        // Use hover-based selection instead of click
        EditorStateMachine.selectSection(sectionId, sectionPosition);
      } else {

        if (!EditorStateMachine.isSectionInEditMode) {
          if (EditorStateMachine.dismissedSectionId !== null) {
            EditorStateMachine.clearDismissedSection();
          }
          EditorStateMachine.deselectSection();
        }
      }
    }
  });

  IframeEventsManager.addEvent(INITIALIZE_WIZARD_SETTINGS_SUCCESS, (event) => {
    Logger.log('IframeEventsManager', 'INITIALIZE_WIZARD_SETTINGS_SUCCESS');

    if (SiteBuilderModel.finalColorScheme) {
      let colorScheme = toJS(SiteBuilderModel.finalColorScheme);

      let colorSchemeMainPayload = Object.keys(colorScheme)
        .filter(
          (key) =>
            !['surface-elevated', 'tertiary', 'warning'].includes(key) &&
            colorScheme[key].main !== void 0
        )
        .map((key) => {
          return {
            color: colorScheme[key].main,
            type: key,
            category: 'main'
          };
        });

      let colorSchemeTextPayload = Object.keys(colorScheme)
        .filter(
          (key) =>
            !['surface-elevated', 'tertiary', 'warning'].includes(key) &&
            colorScheme[key].text !== void 0
        )
        .map((key) => {
          return {
            color: colorScheme[key].text,
            type: key,
            category: 'text'
          };
        });

      event.source.window.postMessage(
        {
          eventName: CHANGE_SITE_COLORS,
          payload: colorSchemeMainPayload.concat(colorSchemeTextPayload)
        },
        '*'
      );
    }

    IframeServiceInjector.reset();
    new WrapperSynchronizer();
    new IframeManager();
    new PageBuilderDataCollector();
    new FrameEventMessanger();

    const iframe = document.getElementById(
      'wizard-iframe'
    ) as HTMLIFrameElement;

    IframeServiceInjector.injectIframe(iframe);

    const pageBuilderDataCollector =
      IframeServiceInjector.getService<PageBuilderDataCollector>(
        'PageBuilderDataCollector'
      );

    pageBuilderDataCollector.initEvents();

    // show the iframe
    // iframe.style.display = 'block';
    // SiteBuilderModel.setIframeLoaded(true);

    setTimeout(() => {
      // fire GET_INITIAL_ELEMENTS and WIZARD_INIT (and restore fonts), then show iframe
      event.source.window.postMessage(
        {
          // this will fire then ELEMENTS_INITIALIZED event
          eventName: GET_INITIAL_ELEMENTS,
          payload: {}
        },
        '*'
      );

      event.source.window.postMessage(
        {
          eventName: WIZARD_INIT,
          payload: {
            // @TODO - move this to INITIALIZE_WIZARD_SETTINGS
            themeVariables: toJS(SiteBuilderModel.generatedThemeConfig),
            siteMap: toJS(SiteMapModel.getSiteMapFinalTree()),
            logo: {
              dimensions: {
                width: DesignModel.selectedLogo?.getDimensions().width,
                height: DesignModel.selectedLogo?.getDimensions().height
              }
            }
          }
        },
        '*'
      );

        // restore selected fonts after iframe reload
        try {
          if (SiteBuilderModel.fontMap) {
            event.source.window.postMessage(
              {
                eventName: CHANGE_SITE_FONTS,
                payload: {
                  mainFont: SiteBuilderModel.fontMap.main?.name,
                  displayFont: SiteBuilderModel.fontMap.display?.name
                }
              },
              '*'
            );
          }
        } catch (e) {
          Logger.log('FrameInit', 'Failed to post CHANGE_SITE_FONTS', e);
        }
      // show the iframe, this prevents font-jump flash
      try {
        iframe.style.display = 'block';
        SiteBuilderModel.setIframeLoaded(true);
      } catch (e) {
        Logger.log('FrameInit', 'Failed to show iframe after init messages', e);
      }
    }, 1000);

    // Listen for edit-mode toggle and forward to renderer iframe
    try {
      window.addEventListener('wizard:section-edit-mode-changed', (ev: any) => {
        const frameEventMessanger = IframeServiceInjector.getService<FrameEventMessanger>('FrameEventMessanger');

        // Send the edit-mode state to renderer so it can style navigation when editing
        frameEventMessanger.sendMessage('WIZARD_EDIT_MODE', {
          isInEditMode: ev?.detail?.isInEditMode || false
        });
      });
      
      // Listen for exit edit mode cleanup (when deselecting section while in edit mode)
      window.addEventListener('wizard:exit-edit-mode-cleanup', (ev: any) => {
        const frameEventMessanger = IframeServiceInjector.getService<FrameEventMessanger>('FrameEventMessanger');
        
        if (ev?.detail?.removeNavigationStyles) {
          frameEventMessanger.sendMessage('WIZARD_EDIT_MODE', {
            isInEditMode: false
          });
        }
      });

      // Listen for Escape key from iframe
      window.addEventListener('message', (ev: any) => {
        if (ev.data.type === 'iframe-escape') {
          EditorStateMachine.deselectSection();
        }
      });
      
      const iframe = document.getElementById('wizard-iframe') as HTMLIFrameElement;
      if (iframe) {
        iframe.addEventListener('mouseleave', (e: MouseEvent) => {
          const relatedTarget = e.relatedTarget as HTMLElement;
          
          if (relatedTarget?.closest('[data-section-overlay]')) {
            return;
          }

          if (!EditorStateMachine.isSectionInEditMode && EditorStateMachine.selectedSectionId !== null) {
            EditorStateMachine.deselectSection();
          }
        });
      }
    } catch (e) {
      Logger.log('FrameInit', 'Failed to attach wizard event listeners', e);
    }
  });

  /**
   * ENTRY POINT FOR WIZARD
   */
  IframeEventsManager.addEvent(WIZARD_BRIDGE_INITIALIZED, async (event) => {
    Logger.log('IframeEventsManager', 'WIZARD_BRIDGE_INITIALIZED');

    event.source.window.postMessage(
      {
        eventName: INITIALIZE_WIZARD_SETTINGS,
        payload: {
          pageGeneratedContent: {
            ...SiteBuilderModel.currentShownInPreviewGeneratedPage.getDataToBeInjectedInThePreview(),
            ...SiteBuilderModel.templateGeneratedData.getDataToBeInjectedInThePreview()
          },

          userPageElements: UserPreviewDataModel.getPageElements(
            SiteBuilderModel.currentShownInPreviewGeneratedPage.getId()
          ),

          userTemplateElements: UserPreviewDataModel.getTemplateElements(),

          ////////////////////////////
          // CONFIG
          ////////////////////////////

          // coming from FinalizeSitemap step. Not implemented yet FinalizeSitemapModel.importantPages
          importantPages: toJS(FinalizeSitemapModel.importantPages),
          // coming from Design step
          websiteLogo: DesignModel.selectedLogo?.getImageSrc(),
          // coming from BusinessDescription step
          socialLinks: toJS(SocialMediaModel.getWizardData())
        }
      },
      '*'
    );
  });

  IframeEventsManager.addEvent(SLIDE_ANIMATION_START, (event) => {
    const { elementPosition, elementId } = event.data.payload;
    EditorStateMachine.transition('onSlideAnimationStart', {
      elementPosition,
      elementId
    });
  });

  IframeEventsManager.addEvent(SLIDE_ANIMATION_START_AFTER_DELAY, (event) => {
    const { elementId } = event.data.payload;
    EditorStateMachine.transition('onSlideAnimationStartAfterDelay', {
      elementId
    });
  });

  IframeEventsManager.addEvent(SLIDE_ANIMATION_END, (event) => {
    const { newPositions, elementId } = event.data.payload;
    // store elements
    EditorStateMachine.initializeElements(newPositions);
    
    // Update selected section position if the changed element is the selected section
    if (EditorStateMachine.selectedSectionId !== null && elementId === EditorStateMachine.selectedSectionId) {
      const updatedSectionElement = newPositions.get(elementId);
      if (updatedSectionElement) {
        EditorStateMachine.updateSectionPosition(updatedSectionElement.elementPosition);
        
        // Also trigger WrapperSynchronizer to update the visual overlay
        const WrapperSynchronizer = IframeServiceInjector.getService<WrapperSynchronizer>('WrapperSynchronizer');
        WrapperSynchronizer.updateElementsPosition(
          `section-${elementId}`,
          updatedSectionElement.elementPosition
        );
        
        if (EditorStateMachine.isSectionInEditMode && elementId === EditorStateMachine.editModeSectionId) {
          IframeServiceInjector.getService<FrameEventMessanger>('FrameEventMessanger').sendMessage(GET_SECTION_EDITABLE_ELEMENTS, {
            sectionId: EditorStateMachine.editModeSectionId
          });
        }
      }
    }
    
    EditorStateMachine.transition('onSlideAnimationFinished', {
      newPositions,
      elementId
    });
  });

  IframeEventsManager.addEvent(TEXT_EDITOR_BLUR, (event) => {
    const { newPositions } = event.data.payload;
    EditorStateMachine.transition('onFinishEditText', { newPositions });
  });

  IframeEventsManager.addEvent(ELEMENT_CHANGED, (event) => {
    const { changedElement } = event.data.payload;

    if (changedElement.resourceType === 'template') {
      UserPreviewDataModel.addTemplateElement(changedElement);
    }

    if (changedElement.resourceType === 'page') {
      UserPreviewDataModel.addPageElement(
        SiteBuilderModel.currentShownInPreviewGeneratedPage.getId(),
        changedElement
      );
    }
  });

  IframeEventsManager.addEvent(SECTION_EDITABLE_ELEMENTS, (event) => {
    const { sectionId, elements, lockedElements } = event.data.payload;
    
    if (!EditorStateMachine.isSectionInEditMode || sectionId !== EditorStateMachine.editModeSectionId) {
      return;
    }
    
    // Store editable and locked elements in EditorStateMachine
    // Only update editable elements if they are provided (not empty array from scroll update)
    if (elements && elements.length > 0) {
      EditorStateMachine.setEditableSectionElements(elements);
    }
    // Always update locked elements (positions change on scroll)
    const safeLockedElements = lockedElements || [];
    
    // Update WrapperSynchronizer with new positions so parent scroll works correctly
    const WrapperSynchronizer = IframeServiceInjector.getService<WrapperSynchronizer>('WrapperSynchronizer');
    safeLockedElements.forEach((el, index) => {
      const uniqueKey = `${sectionId}-${el.elementType}-${index}`;
      WrapperSynchronizer.updateElementsPosition(
        `locked-${uniqueKey}`,
        el.elementPosition
      );
    });

    EditorStateMachine.setLockedSectionElements(safeLockedElements);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    const iframe = document.getElementById(
      'wizard-iframe'
    ) as HTMLIFrameElement | null;

    if (!iframe?.contentWindow || !SiteBuilderModel.iframeLoaded) {
      return;
    }

    const frameEventMessanger =
      IframeServiceInjector.getService<FrameEventMessanger>(
        'FrameEventMessanger'
      );

    frameEventMessanger.sendMessage(GET_INITIAL_ELEMENTS, {});
  });
}
